// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import Ractive from 'ractive';
import React from 'react';
import ReactDOM from 'react-dom/client';
class RactiveAdaptor extends Ractive {
    constructor() {
        super(...arguments);
        this.reactRoot = null;
        this.parentAdapter = null;
    }
    onrender() {
        this.initializeReactComponent();
        this.renderReactComponent(this);
    }
    oninit() {
        if (isNullOrUndefined(this.reactComponent)) {
            return;
        }
        this.observe('*', () => {
            this.renderReactComponent(this);
        });
    }
    onteardown() {
        if (!isNullOrUndefined(this.reactRoot)) {
            this.reactRoot.unmount();
        }
    }
    initializeReactComponent() {
        if (!isNullOrUndefined(this.parentAdapter) || !this.rendered) {
            return;
        }
        const container = this.find(this.mountContainer);
        if (isNullOrUndefined(container)) {
            return;
        }
        this.reactRoot = ReactDOM.createRoot(container);
    }
    renderReactComponent(adapter) {
        if (isNullOrUndefined(this.reactRoot)) {
            if (!isNullOrUndefined(this.parentAdapter)) {
                this.parentAdapter.renderReactComponent(this.parentAdapter);
            }
            return;
        }
        const props = adapter.createReactProps();
        this.reactRoot.render(React.createElement(adapter.reactComponent, props));
    }
    createReactProps() {
        const props = Object.assign({}, this.get());
        if (isNullOrUndefined(props.ref) === false) {
            props.ref = this.get('ref', { virtual: false, unwrap: true });
        }
        const subs = this._subs;
        if (isNullOrUndefined(subs)) {
            return;
        }
        for (const eventName of Object.keys(subs)) {
            if (eventName.startsWith('*')) {
                continue;
            }
            props[eventName] = (...args) => {
                this.fire(eventName, {}, ...args);
            };
        }
        return props;
    }
}
class RactiveDecoratorAdaptor extends RactiveAdaptor {
    constructor() {
        super(...arguments);
        this.parentAdapter = null;
    }
    onrender() {
        this.initializeReactComponent();
        this.renderReactComponent(this);
    }
    initializeReactComponent() {
        super.initializeReactComponent();
        const children = this.findAllComponents();
        for (const child of children) {
            child.parentAdapter = isNullOrUndefined(this.parentAdapter) ? this : this.parentAdapter;
        }
    }
    renderReactComponent(adapter) {
        const ReactComponent = adapter.reactComponent;
        const props = adapter.createReactProps();
        const childElements = this.getChildReactElements(this);
        if (isNullOrUndefined(this.reactRoot)) {
            return;
        }
        this.reactRoot.render(React.createElement(ReactComponent, props, ...childElements));
    }
    getChildReactElements(ractive) {
        var _a;
        const elements = [];
        const children = ractive.findAllComponents();
        if (!isValidArray(children)) {
            return elements;
        }
        for (const child of children) {
            if (((_a = child.container) === null || _a === void 0 ? void 0 : _a.component) !== ractive.component) {
                continue;
            }
            if (child instanceof RactiveDecoratorAdaptor) {
                const nestedElements = this.getChildReactElements(child);
                elements.push(React.createElement(child.reactComponent, Object.assign(Object.assign({}, child.createReactProps()), { key: child._guid }), ...nestedElements));
            }
            else {
                elements.push(React.createElement(child.reactComponent, Object.assign(Object.assign({}, child.createReactProps()), { key: child._guid })));
            }
        }
        return elements;
    }
}
export function createReactAdapter(component) {
    return RactiveAdaptor.extend({
        template: '<span class="react-adaptor"></span>',
        mountContainer: '.react-adaptor',
        reactComponent: component
    });
}
export function createReactDecoratorAdapter(component) {
    return RactiveDecoratorAdaptor.extend({
        template: '<span class="react-decorator-adaptor">{{yield}}</span>',
        mountContainer: '.react-decorator-adaptor',
        reactComponent: component
    });
}
